import { operationsBase, TABLES, fetchFAQsForClient } from './airtable.js';

/**
 * Partner Portal API
 * 
 * Provides secure token-based access to client program data
 * without requiring user authentication.
 */

// Helper function to escape user input for Airtable formula strings
const escapeAirtableString = (str) => {
  if (typeof str !== 'string') {
    return '';
  }
  return str.replace(/'/g, "\\'");
};

/**
 * Validate portal token format
 * Expected format: pk_live_[base64url]
 */
export const isValidTokenFormat = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  // Check format: pk_live_ prefix followed by base64url characters
  const tokenRegex = /^pk_live_[A-Za-z0-9_-]+$/;
  return tokenRegex.test(token) && token.length > 15; // Minimum reasonable length
};

/**
 * Get partner/client information by portal token
 * Returns client record if token is valid and portal is enabled
 */
export const getPartnerByToken = async (token) => {
  if (!isValidTokenFormat(token)) {
    throw new Error('Invalid token format');
  }
  
  try {
    const clients = await operationsBase(TABLES.CLIENTS)
      .select({
        filterByFormula: `AND(
          {portal_token} = '${escapeAirtableString(token)}',
          {portal_enabled} = TRUE()
        )`,
        maxRecords: 1,
        fields: [
          'ClientID',
          'Client Long Name', 
          'Status',
          'portal_token',
          'portal_enabled',
          'Currency'
        ]
      })
      .firstPage();
    
    if (clients.length === 0) {
      throw new Error('Invalid or disabled token');
    }
    
    const client = clients[0];
    
    return {
      id: client.id,
      clientId: client.fields['ClientID'],
      name: client.fields['Client Long Name'],
      status: client.fields['Status'],
      currency: client.fields['Currency'] || 'CAD',
      portalEnabled: client.fields['portal_enabled']
    };
    
  } catch (error) {
    if (error.message === 'Invalid or disabled token') {
      throw error;
    }
    console.error('Error fetching partner by token:', error);
    throw new Error('Failed to validate token');
  }
};

/**
 * Get all programs (Scheduled Offerings) for a client
 * Returns programs with basic info and session counts
 */
export const getClientPrograms = async (clientId) => {
  try {
    
    // First try to get all SOs and filter them manually (like main codebase does)
    const scheduledOfferings = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
      .select({
        fields: [
          'SOID',
          'SO',
          'ProgramName',
          'Client Name',
          'Participating Clients',
          'Client',
          'SO Status',
          'Type',
          'Language',
          'Delivery Start',
          'Delivery End',
          'Sessions',
          'PL Name',
          'Virtual Platform'
        ],
        sort: [
          { field: 'Delivery Start', direction: 'asc' }
        ]
      })
      .all();
    
    // Filter client SOs using the same logic as main codebase
    const clientSOs = scheduledOfferings.filter(so => {
      // Try Participating Clients first (new unified field), fallback to Client (legacy)
      const participatingClientIds = so.get('Participating Clients') || [];
      const legacyClientIds = so.get('Client') || [];
      
      // Check both fields to handle migration period
      const allClientIds = [...participatingClientIds, ...legacyClientIds];
      
      const isClientSO = Array.isArray(allClientIds) && allClientIds.includes(clientId);
      
      if (!isClientSO) return false;
      
      // Hide cancelled SOs unless client is Bell
      const status = so.get('SO Status');
      const isCancelled = status && status.toLowerCase().includes('cancel');
      
      if (isCancelled) {
        // Only show cancelled SOs for Bell client
        // Get client name to check if it's Bell
        const clientName = so.get('Client Name');
        const isBellClient = clientName && clientName.toLowerCase().includes('bell');
        return isBellClient;
      }
      
      return true;
    });
    
    const scheduledOfferingsFiltered = clientSOs;
    
    // Process programs and count sessions
    const programs = await Promise.all(
      scheduledOfferingsFiltered.map(async (so) => {
        const fields = so.fields;
        
        // Count sessions for this SO
        const sessionCount = fields['Sessions'] ? fields['Sessions'].length : 0;
        
        // Count attendees for this SO  
        const attendeeCount = await getAttendeeCountForSO(fields['SO']);
        
        return {
          id: so.id,
          soid: fields['SOID'],
          soNumber: fields['SO'],
          programName: fields['ProgramName'],
          status: fields['SO Status'],
          type: fields['Type'],
          language: fields['Language'],
          deliveryStart: fields['Delivery Start'],
          deliveryEnd: fields['Delivery End'],
          sessionCount,
          attendeeCount,
          programLeader: fields['PL Name'],
          virtualPlatform: fields['Virtual Platform']
        };
      })
    );
    
    return programs;
    
  } catch (error) {
    console.error('Error fetching client programs:', error);
    throw new Error('Failed to fetch programs');
  }
};

/**
 * Get attendee count for a specific SO
 */
const getAttendeeCountForSO = async (soNumber) => {
  if (!soNumber) return 0;
  
  try {
    const attendees = await operationsBase(TABLES.ATTENDEES)
      .select({
        filterByFormula: `{SO} = '${escapeAirtableString(soNumber)}'`,
        fields: ['AttendeeID'],
        maxRecords: 1000 // Reasonable limit
      })
      .all();
    
    return attendees.length;
  } catch (error) {
    console.error(`Error counting attendees for SO ${soNumber}:`, error);
    return 0;
  }
};

/**
 * Get comprehensive metrics for a client
 * Includes program count, total attendees, and NPS average
 */
export const getClientMetrics = async (clientId, programs = null) => {
  try {
    // Use provided programs or fetch them
    const clientPrograms = programs || await getClientPrograms(clientId);
    
    // Calculate basic metrics
    const programCount = clientPrograms.length;
    const totalAttendees = clientPrograms.reduce((sum, program) => sum + program.attendeeCount, 0);
    
    // Calculate NPS average across all programs
    const npsAverage = await calculateClientNPS();
    
    // Get completed vs in-progress programs
    const completedPrograms = clientPrograms.filter(p => 
      p.status && (p.status.toLowerCase().includes('complete') || p.status.toLowerCase().includes('delivered'))
    ).length;
    
    const inProgressPrograms = clientPrograms.filter(p => 
      p.status && (p.status.toLowerCase().includes('progress') || p.status.toLowerCase().includes('active'))
    ).length;
    
    return {
      programCount,
      totalAttendees,
      npsAverage,
      completedPrograms,
      inProgressPrograms,
      upcomingPrograms: programCount - completedPrograms - inProgressPrograms
    };
    
  } catch (error) {
    console.error('Error calculating client metrics:', error);
    return {
      programCount: 0,
      totalAttendees: 0,
      npsAverage: null,
      completedPrograms: 0,
      inProgressPrograms: 0,
      upcomingPrograms: 0
    };
  }
};

/**
 * Calculate average NPS score for client programs
 * Uses Typeform integration to get survey responses
 */
const calculateClientNPS = async () => {
  // For now, return null - NPS calculation would require:
  // 1. Mapping programs to their Typeform survey IDs
  // 2. Fetching survey responses for each program
  // 3. Calculating NPS from the responses
  // This can be implemented in a future iteration
  
  // Placeholder for future NPS implementation
  return null;
};

/**
 * Get complete partner dashboard data
 * Combines client info, programs, and metrics
 */
export const getPartnerDashboardData = async (token) => {
  try {
    // Validate token and get client
    const client = await getPartnerByToken(token);
    
    // Get programs for this client
    const programs = await getClientPrograms(client.id);
    
    // Calculate metrics and fetch FAQs in parallel (after programs)
    const [metrics, faqsResult] = await Promise.all([
      getClientMetrics(client.id, programs),
      fetchFAQsForClient(client.id, programs).catch((e) => {
        console.warn('Failed to fetch FAQs, continuing without them:', e);
        return {};
      }),
    ]);
    const faqs = faqsResult;
    
    return {
      client,
      programs,
      metrics,
      faqs,
      lastUpdated: new Date().toISOString()
    };
    
  } catch (error) {
    console.error('Error fetching partner dashboard data:', error);
    throw error; // Re-throw to let caller handle
  }
};

/**
 * Validate token and return boolean
 * Lighter weight check for token validity
 */
export const validatePortalToken = async (token) => {
  try {
    await getPartnerByToken(token);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get list of active programs for filtering
 */
export const getActivePrograms = async () => {
  try {
    const programs = await operationsBase(TABLES.PROGRAMS)
      .select({
        filterByFormula: `AND({Status} = "Released", {Current} = TRUE())`,
        fields: ['Full Program Name', 'ProgramID'],
        sort: [{ field: 'Full Program Name', direction: 'asc' }]
      })
      .all();
    
    return programs.map(program => ({
      id: program.id,
      name: program.fields['Full Program Name'],
      programId: program.fields['ProgramID']
    }));
  } catch (error) {
    console.error('Error fetching active programs:', error);
    return [];
  }
};

/**
 * Generate partner portal URL for a given token
 * Utility function for admin interface
 */
export const generatePortalUrl = (token, baseUrl = window.location.origin) => {
  if (!isValidTokenFormat(token)) {
    throw new Error('Invalid token format');
  }
  
  return `${baseUrl}/p/${token}`;
};

// Export all functions
export default {
  isValidTokenFormat,
  getPartnerByToken,
  getClientPrograms,
  getClientMetrics,
  getPartnerDashboardData,
  validatePortalToken,
  getActivePrograms,
  generatePortalUrl
};